/**
 * GPT-5 family: reasoning effort vs temperature rules for Connector / AgentConfigurator.
 * Backend field: models_reasoning (e.g. "none", "medium", "xhigh").
 */

export type Gpt5ModelSpec = {
  reasoningOptions: readonly string[];
  defaultReasoning: string;
  /** If true, temperature is never used (omit from API payload). */
  hideTemperatureAlways: boolean;
  /** If false and hideTemperatureAlways is false, temperature only when models_reasoning === "none". */
  hint: string;
};

const SPECS: Record<string, Gpt5ModelSpec> = {
  'gpt-5': {
    reasoningOptions: ['minimal', 'low', 'medium', 'high'],
    defaultReasoning: 'medium',
    hideTemperatureAlways: true,
    hint: 'Temperature is not used. Reasoning: minimal, low, medium (default), high.',
  },
  'gpt-5-mini': {
    reasoningOptions: ['minimal', 'low', 'medium', 'high'],
    defaultReasoning: 'medium',
    hideTemperatureAlways: true,
    hint: 'Temperature is not used. Reasoning: minimal, low, medium (default), high.',
  },
  'gpt-5-nano': {
    reasoningOptions: ['minimal', 'low', 'medium', 'high'],
    defaultReasoning: 'medium',
    hideTemperatureAlways: true,
    hint: 'Temperature is not used. Reasoning: minimal, low, medium (default), high.',
  },
  'gpt-5.1': {
    reasoningOptions: ['none', 'low', 'medium', 'high'],
    defaultReasoning: 'none',
    hideTemperatureAlways: false,
    hint: 'Temperature is available only when reasoning is none (default). Otherwise use reasoning effort.',
  },
  'gpt-5.2': {
    reasoningOptions: ['none', 'low', 'medium', 'high', 'xhigh'],
    defaultReasoning: 'none',
    hideTemperatureAlways: false,
    hint: 'Temperature only when reasoning is none (default). Reasoning includes xhigh.',
  },
  'gpt-5.5': {
    reasoningOptions: ['none', 'low', 'medium', 'high', 'xhigh'],
    defaultReasoning: 'medium',
    hideTemperatureAlways: false,
    hint: 'Temperature only when reasoning is none. Reasoning: none, low, medium (default), high, xhigh.',
  },
  'gpt-5.4': {
    reasoningOptions: ['none', 'low', 'medium', 'high', 'xhigh'],
    defaultReasoning: 'none',
    hideTemperatureAlways: false,
    hint: 'Temperature only when reasoning is none (default). Reasoning includes xhigh.',
  },
  'gpt-5.4-mini': {
    reasoningOptions: ['none', 'low', 'medium', 'high', 'xhigh'],
    defaultReasoning: 'none',
    hideTemperatureAlways: false,
    hint: 'Temperature only when reasoning is none. Reasoning: none, low, medium, high, xhigh.',
  },
  'gpt-5.4-nano': {
    reasoningOptions: ['none', 'low', 'medium', 'high', 'xhigh'],
    defaultReasoning: 'none',
    hideTemperatureAlways: false,
    hint: 'Temperature only when reasoning is none. Reasoning: none, low, medium, high, xhigh.',
  },
};

/** Stable dropdown order for GPT-5 family models in configurators. */
export const GPT5_CONFIGURATOR_MODEL_IDS = [
  'gpt-5',
  'gpt-5-mini',
  'gpt-5-nano',
  'gpt-5.1',
  'gpt-5.2',
  'gpt-5.5',
  'gpt-5.4',
  'gpt-5.4-mini',
  'gpt-5.4-nano',
] as const;

for (const modelId of GPT5_CONFIGURATOR_MODEL_IDS) {
  if (!SPECS[modelId]) {
    throw new Error(`Missing GPT-5 configurator spec for ${modelId}`);
  }
}

export function getGpt5ModelSpec(model: string): Gpt5ModelSpec | null {
  const key = (model || '').trim();
  return SPECS[key] ?? null;
}

export function resolveModelsReasoning(model: string, raw: unknown): string | undefined {
  const spec = getGpt5ModelSpec(model);
  if (!spec) return undefined;
  const s = typeof raw === 'string' ? raw.trim() : '';
  if (s && spec.reasoningOptions.includes(s)) return s;
  return spec.defaultReasoning;
}

export function shouldShowTemperature(model: string, modelsReasoning: string | undefined): boolean {
  const spec = getGpt5ModelSpec(model);
  if (!spec) return true;
  if (spec.hideTemperatureAlways) return false;
  return modelsReasoning === 'none';
}

/** Short hint when the temperature slider is shown but disabled (GPT-5 rules). */
export function getTemperatureInactiveHint(model: string, modelsReasoning: string | undefined): string | null {
  if (shouldShowTemperature(model, modelsReasoning)) return null;
  const spec = getGpt5ModelSpec(model);
  if (!spec) return null;
  if (spec.hideTemperatureAlways) {
    return 'Temperature is not used for this model; it is not sent in the optimization payload.';
  }
  return 'Temperature is only used when reasoning is set to "none". Change reasoning above to enable it.';
}

export function reasoningOptionLabel(option: string, defaultReasoning: string): string {
  return option === defaultReasoning ? `${option} (default)` : option;
}

/**
 * OpenAI does not support fine-tuning on GPT-5 family base models.
 * Used to disable trainable toggles and filter training job payloads.
 */
export function isBaseModelUnsupportedForFineTuning(modelId: string | null | undefined): boolean {
  const raw = (modelId ?? '').trim();
  if (!raw) return false;
  if (getGpt5ModelSpec(raw) != null) return true;
  const lower = raw.toLowerCase();
  if (lower === 'gpt-5') return true;
  // Dated / snapshot ids, e.g. gpt-5-2025-08-07
  if (/^gpt-5[.-]/.test(lower)) return true;
  return false;
}

/** Agent is included in a fine-tuning job only if marked trainable and base model supports FT. */
export function isEligibleForFineTuningJob(
  isTrainable: boolean | undefined,
  baseModel: string | null | undefined
): boolean {
  return isTrainable !== false && !isBaseModelUnsupportedForFineTuning(baseModel);
}
